// SEO Utils für dynamische Meta-Tags und Structured Data
export interface SEOConfig {
  title: string;
  description: string;
  keywords: string[];
  canonicalUrl?: string;
  ogImage?: string;
}

// SEO-optimierte Inhalte für spezifische Reparaturen
export const getSEOConfig = (deviceBrand?: string, deviceModel?: string, repairType?: string): SEOConfig => {
  const baseKeywords = ['PhoneMedic', 'Handy Reparatur', 'Express Service', '12 Monate Garantie'];

  // Für spezifische Geräte-Reparatur-Seiten
  if (deviceBrand && deviceModel) {
    const deviceName = `${deviceBrand} ${deviceModel}`;
    const title = `${deviceName} Reparatur - Express Service | PhoneMedic`;
    const description = `${deviceName} Reparatur ✓ Display Austausch ✓ Akku tauschen ✓ Express 24h Service ✓ 12 Monate Garantie ✓ Kostenloser Versand ab 50€ | PhoneMedic`;

    const keywords = [
      ...baseKeywords,
      `${deviceName} Reparatur`,
      `${deviceName} Display`,
      `${deviceName} Akku`,
      `${deviceBrand} Reparatur`,
      `${deviceModel} reparieren`
    ];

    if (repairType) {
      keywords.push(`${deviceName} ${repairType}`);
    }

    return {
      title,
      description,
      keywords,
      canonicalUrl: `https://www.phone-medic.de/repair/${deviceBrand.toLowerCase()}/${deviceModel.toLowerCase().replace(/\s+/g, '-')}`
    };
  }

  // Für Marken-spezifische Seiten
  if (deviceBrand) {
    const title = `${deviceBrand} Reparatur - Alle Modelle | PhoneMedic Express Service`;
    const description = `${deviceBrand} Reparatur aller Modelle ✓ Display ✓ Akku ✓ Kamera ✓ Express 24h ✓ 12 Monate Garantie ✓ Kostenloser Versand | PhoneMedic`;

    const keywords = [
      ...baseKeywords,
      `${deviceBrand} Reparatur`,
      `${deviceBrand} Display Reparatur`,
      `${deviceBrand} Akku tauschen`,
      `${deviceBrand} Service`
    ];

    return {
      title,
      description,
      keywords,
      canonicalUrl: `https://www.phone-medic.de/repair/${deviceBrand.toLowerCase()}`
    };
  }

  // Default Homepage SEO - MacBook prioritized
  return {
    title: 'PhoneMedic - MacBook Reparatur, iPhone Reparatur, Laptop Reparatur Express Service',
    description: 'PhoneMedic ✓ MacBook Reparatur ✓ MacBook Pro M4 Reparatur ✓ MacBook Air Reparatur ✓ iPhone Reparatur ✓ Express 24h Service ✓ 12 Monate Garantie ✓ Kostenloser Versand ab 50€. Professionelle Laptop Reparatur!',
    keywords: [
      ...baseKeywords,
      'MacBook Reparatur',
      'MacBook Pro Reparatur',
      'MacBook Air Reparatur',
      'Laptop Reparatur',
      'MacBook Display Austausch',
      'MacBook Akku tauschen',
      'iPhone Reparatur',
      'Apple Reparatur Service'
    ],
    canonicalUrl: 'https://www.phone-medic.de'
  };
};

// Strukturierte Daten für spezifische Services generieren
export const generateServiceStructuredData = (deviceBrand: string, deviceModel: string, price: number) => {
  return {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": `${deviceBrand} ${deviceModel} Reparatur`,
    "description": `Professionelle ${deviceBrand} ${deviceModel} Reparatur mit Express Service`,
    "provider": {
      "@type": "LocalBusiness",
      "name": "PhoneMedic",
      "url": "https://www.phone-medic.de"
    },
    "offers": {
      "@type": "Offer",
      "price": price,
      "priceCurrency": "EUR",
      "availability": "https://schema.org/InStock",
      "priceValidUntil": "2026-12-31",
      "warranty": {
        "@type": "WarrantyPromise",
        "durationOfWarranty": "P12M"
      }
    },
    "serviceType": "Elektronik Reparatur",
    "areaServed": "DE"
  };
};

// Meta-Tags dynamisch setzen
export const updateMetaTags = (seoConfig: SEOConfig) => {
  // Title setzen
  document.title = seoConfig.title;

  // Description setzen
  updateMetaTag('name', 'description', seoConfig.description);

  // Keywords setzen
  updateMetaTag('name', 'keywords', seoConfig.keywords.join(', '));

  // Canonical URL setzen
  if (seoConfig.canonicalUrl) {
    updateLinkTag('rel', 'canonical', seoConfig.canonicalUrl);
  }

  // Open Graph Tags aktualisieren
  updateMetaTag('property', 'og:title', seoConfig.title);
  updateMetaTag('property', 'og:description', seoConfig.description);
  if (seoConfig.canonicalUrl) {
    updateMetaTag('property', 'og:url', seoConfig.canonicalUrl);
  }

  // Twitter Card Tags
  updateMetaTag('name', 'twitter:title', seoConfig.title);
  updateMetaTag('name', 'twitter:description', seoConfig.description);
};

// Helper-Funktionen
const updateMetaTag = (attribute: string, value: string, content: string) => {
  let tag = document.querySelector(`meta[${attribute}="${value}"]`);
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attribute, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute('content', content);
};

const updateLinkTag = (attribute: string, value: string, href: string) => {
  let tag = document.querySelector(`link[${attribute}="${value}"]`);
  if (!tag) {
    tag = document.createElement('link');
    tag.setAttribute(attribute, value);
    document.head.appendChild(tag);
  }
  tag.setAttribute('href', href);
};

// Beliebte Suchbegriffe für besseres SEO Ranking - MacBook prioritized
export const popularSearchTerms = [
  'MacBook Pro Reparatur',
  'MacBook Air Reparatur',
  'MacBook Display Reparatur',
  'MacBook Akku tauschen',
  'MacBook Pro M4 Reparatur',
  'MacBook Pro 16 Reparatur',
  'MacBook Pro 14 Reparatur',
  'MacBook Tastatur Reparatur',
  'MacBook Logicboard Reparatur',
  'Laptop Reparatur Express',
  'iPhone 14 Reparatur',
  'Samsung Galaxy S24 Reparatur',
  'Handy Display kaputt',
  'iPhone Display tauschen',
  'Samsung Display Reparatur',
  'Handy Akku tauschen',
  'iPhone Kamera Reparatur',
  'Smartphone Express Reparatur',
  'Handy Reparatur 24 Stunden',
  'iPhone Reparatur Express',
  'Samsung Reparatur schnell'
];